export interface VisualColumn {
  label: string;
  bracket: "winners" | "losers" | "finals";
  rounds: number[];
  matchupCount: number;
}

export interface BracketLayout {
  winnersColumns: VisualColumn[];
  losersColumns: VisualColumn[];
  finalsColumn: VisualColumn | null;
}

export function getBracketLayout(
  bracketSize: number,
  gameType: string,
): BracketLayout {
  if (gameType === "SingleE") {
    return getSingleEliminationLayout(bracketSize);
  }
  return getDoubleEliminationLayout(bracketSize);
}

function getSingleEliminationLayout(bracketSize: number): BracketLayout {
  const columns: VisualColumn[] = [];
  let roundStart = 1;
  let matchups = bracketSize / 2;
  let colIdx = 0;
  const labels = getSEColumnLabels(bracketSize);

  while (matchups >= 1) {
    const rounds = Array.from(
      { length: matchups },
      (_, i) => roundStart + i,
    );
    columns.push({
      label: labels[colIdx] || `Round ${colIdx + 1}`,
      bracket: "winners",
      rounds,
      matchupCount: matchups,
    });
    roundStart += matchups;
    matchups = Math.floor(matchups / 2);
    colIdx++;
  }

  return { winnersColumns: columns, losersColumns: [], finalsColumn: null };
}

function getSEColumnLabels(bracketSize: number): string[] {
  switch (bracketSize) {
    case 8:
      return ["Quarterfinals", "Semifinals", "Finals"];
    case 16:
      return ["Round 1", "Quarterfinals", "Semifinals", "Finals"];
    case 32:
      return ["Round 1", "Round 2", "Quarterfinals", "Semifinals", "Finals"];
    case 64:
      return [
        "Round 1",
        "Round 2",
        "Round 3",
        "Quarterfinals",
        "Semifinals",
        "Finals",
      ];
    default:
      return [];
  }
}

function getDoubleEliminationLayout(bracketSize: number): BracketLayout {
  switch (bracketSize) {
    case 8:
      return getDE8Layout();
    case 16:
      return getDE16Layout();
    case 32:
      return getDE32Layout();
    case 64:
      return getDE64Layout();
    default:
      return { winnersColumns: [], losersColumns: [], finalsColumn: null };
  }
}

function range(start: number, count: number): number[] {
  return Array.from({ length: count }, (_, i) => start + i);
}

function getDE8Layout(): BracketLayout {
  return {
    winnersColumns: [
      { label: "Winners R1", bracket: "winners", rounds: range(1, 4), matchupCount: 4 },
      { label: "Winners R2", bracket: "winners", rounds: range(7, 2), matchupCount: 2 },
      { label: "Winners Final", bracket: "winners", rounds: [12], matchupCount: 1 },
    ],
    losersColumns: [
      { label: "Losers R1", bracket: "losers", rounds: range(5, 2), matchupCount: 2 },
      { label: "Losers R2", bracket: "losers", rounds: range(9, 2), matchupCount: 2 },
      { label: "Losers R3", bracket: "losers", rounds: [11], matchupCount: 1 },
      { label: "Losers Final", bracket: "losers", rounds: [13], matchupCount: 1 },
    ],
    finalsColumn: { label: "Grand Final", bracket: "finals", rounds: [14], matchupCount: 1 },
  };
}

function getDE16Layout(): BracketLayout {
  return {
    winnersColumns: [
      { label: "Winners R1", bracket: "winners", rounds: range(1, 8), matchupCount: 8 },
      { label: "Winners R2", bracket: "winners", rounds: range(13, 4), matchupCount: 4 },
      { label: "Winners R3", bracket: "winners", rounds: range(23, 2), matchupCount: 2 },
      { label: "Winners Final", bracket: "winners", rounds: [28], matchupCount: 1 },
    ],
    losersColumns: [
      { label: "Losers R1", bracket: "losers", rounds: range(9, 4), matchupCount: 4 },
      { label: "Losers R2", bracket: "losers", rounds: range(17, 4), matchupCount: 4 },
      { label: "Losers R3", bracket: "losers", rounds: range(21, 2), matchupCount: 2 },
      { label: "Losers R4", bracket: "losers", rounds: range(25, 2), matchupCount: 2 },
      { label: "Losers R5", bracket: "losers", rounds: [27], matchupCount: 1 },
      { label: "Losers Final", bracket: "losers", rounds: [29], matchupCount: 1 },
    ],
    finalsColumn: { label: "Grand Final", bracket: "finals", rounds: [30], matchupCount: 1 },
  };
}

function getDE32Layout(): BracketLayout {
  return {
    winnersColumns: [
      { label: "Winners R1", bracket: "winners", rounds: range(1, 16), matchupCount: 16 },
      { label: "Winners R2", bracket: "winners", rounds: range(25, 8), matchupCount: 8 },
      { label: "Winners R3", bracket: "winners", rounds: range(45, 4), matchupCount: 4 },
      { label: "Winners R4", bracket: "winners", rounds: range(55, 2), matchupCount: 2 },
      { label: "Winners Final", bracket: "winners", rounds: [60], matchupCount: 1 },
    ],
    losersColumns: [
      { label: "Losers R1", bracket: "losers", rounds: range(17, 8), matchupCount: 8 },
      { label: "Losers R2", bracket: "losers", rounds: range(33, 8), matchupCount: 8 },
      { label: "Losers R3", bracket: "losers", rounds: range(41, 4), matchupCount: 4 },
      { label: "Losers R4", bracket: "losers", rounds: range(49, 4), matchupCount: 4 },
      { label: "Losers R5", bracket: "losers", rounds: range(53, 2), matchupCount: 2 },
      { label: "Losers R6", bracket: "losers", rounds: range(57, 2), matchupCount: 2 },
      { label: "Losers R7", bracket: "losers", rounds: [59], matchupCount: 1 },
      { label: "Losers Final", bracket: "losers", rounds: [61], matchupCount: 1 },
    ],
    finalsColumn: { label: "Grand Final", bracket: "finals", rounds: [62], matchupCount: 1 },
  };
}

function getDE64Layout(): BracketLayout {
  return {
    winnersColumns: [
      { label: "Winners R1", bracket: "winners", rounds: range(1, 32), matchupCount: 32 },
      { label: "Winners R2", bracket: "winners", rounds: range(49, 16), matchupCount: 16 },
      { label: "Winners R3", bracket: "winners", rounds: range(89, 8), matchupCount: 8 },
      { label: "Winners R4", bracket: "winners", rounds: range(109, 4), matchupCount: 4 },
      { label: "Winners R5", bracket: "winners", rounds: range(119, 2), matchupCount: 2 },
      { label: "Winners Final", bracket: "winners", rounds: [124], matchupCount: 1 },
    ],
    losersColumns: [
      { label: "Losers R1", bracket: "losers", rounds: range(33, 16), matchupCount: 16 },
      { label: "Losers R2", bracket: "losers", rounds: range(65, 16), matchupCount: 16 },
      { label: "Losers R3", bracket: "losers", rounds: range(81, 8), matchupCount: 8 },
      { label: "Losers R4", bracket: "losers", rounds: range(97, 8), matchupCount: 8 },
      { label: "Losers R5", bracket: "losers", rounds: range(105, 4), matchupCount: 4 },
      { label: "Losers R6", bracket: "losers", rounds: range(113, 4), matchupCount: 4 },
      { label: "Losers R7", bracket: "losers", rounds: range(117, 2), matchupCount: 2 },
      { label: "Losers R8", bracket: "losers", rounds: range(121, 2), matchupCount: 2 },
      { label: "Losers R9", bracket: "losers", rounds: [123], matchupCount: 1 },
      { label: "Losers Final", bracket: "losers", rounds: [125], matchupCount: 1 },
    ],
    finalsColumn: { label: "Grand Final", bracket: "finals", rounds: [126], matchupCount: 1 },
  };
}
